import React from 'react';
import classNames from 'classnames';
import { addHours, addYears } from 'date-fns';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { FaCalendarAlt } from 'react-icons/fa';
import { CalendarPague, InputNumberPague } from '../../../../../../../styles/primeReactConfig.js';
import SimulacaoResumoCard from '../sections/SimulacaoResumoCard.jsx';
import { getRecebivelDropdownPt } from '../shared/PrimeDropdownProps.js';

/**
 * @typedef {object} RecebivelSimulacaoDialogProps
 * @property {boolean} visible
 * @property {() => void} onShow
 * @property {() => void} onHide
 * @property {(event: React.FormEvent<HTMLFormElement>) => void} onSubmit
 * @property {() => void} onConfirmSimulacao
 * @property {object} formState
 * @property {Date|string|null} formState.recebivelTipoDataInicio
 * @property {number|string|null} formState.recebivelTipoValor
 * @property {(value: Date|string|null) => void} formState.setRecebivelTipoDataInicio
 * @property {(value: number|string|null) => void} formState.setRecebivelTipoValor
 * @property {object} simulacaoState
 * @property {string} simulacaoState.recebivelTipoDescontoTipoSimula
 * @property {string|number|null} simulacaoState.recebivelTipoDescontoSimula
 * @property {number|string|null} simulacaoState.simulaParcela
 * @property {boolean} simulacaoState.simulaParcelaValida
 * @property {boolean} simulacaoState.descontoAtivado
 * @property {number|string|null} simulacaoState.parcelaSimulado
 * @property {number|string|null} simulacaoState.valorParcela
 * @property {string} simulacaoState.mostraDateSimulado
 * @property {object} actions
 * @property {(value: string) => void} actions.setRecebivelTipoDescontoTipoSimula
 * @property {(value: string|number|null) => void} actions.setRecebivelTipoDescontoSimula
 * @property {(value: number|string|null) => void} actions.setSimulaParcela
 * @property {(rawValue: string|number|null|undefined) => void} [actions.handleSimulaParcelaInputChange]
 * @property {{label: string, value: string}[]} descontoOptions
 */

/**
 * Dialog de simulacao local de parcelas.
 *
 * @param {RecebivelSimulacaoDialogProps} props
 * @returns {JSX.Element}
 */
export default function RecebivelSimulacaoDialog(props) {
    const {
        visible,
        onShow,
        onHide,
        onSubmit,
        onConfirmSimulacao,
        formState,
        simulacaoState,
        actions,
        descontoOptions,
    } = props;

    return (
        <Dialog
            header='Simular Parcela'
            visible={visible}
            onShow={onShow}
            onHide={onHide}
            pt={{
                root: 'w-screen md:w-[70vw] max-h-[100vh]',
                header: classNames('flex items-center justify-between shrink-0',
                    'bg-white text-gray-800 border-t-0  rounded-tl-lg rounded-tr-lg p-6',
                    'dark:bg-gray-900  dark:text-white/80'),
                content: ({ state }) => ({
                    className: classNames('overflow-auto w-full max-h-[85vh]',
                        'bg-white text-gray-700 px-6 pb-8 pt-0',
                        'rounded-bl-lg rounded-br-lg',
                        'dark:bg-gray-900 dark:text-white/80 ', {
                        'grow': state.maximized
                    })
                }),
            }}
        >
            <form onSubmit={onSubmit}>
                <div className="flex flex-wrap justify-between gap-2 mt-3">
                    <div className='w-full flex md:flex-row flex-col gap-4'>
                        <div className="flex flex-col w-full md:w-1/2 gap-2">
                            <label htmlFor="recebivel_tipo_data_inicio" className='mt-2 mb-2'>Vencimento da primeira parcela</label>
                            <Calendar
                                className='formularioAdimplencia'
                                locale="pt-BR"
                                dateFormat="dd/mm/yy"
                                id="recebivel_tipo_data_inicio"
                                placeholder="ex: 25/05/2022"
                                mask="99/99/9999"
                                value={formState.recebivelTipoDataInicio}
                                onChange={(e) => formState.setRecebivelTipoDataInicio(e.value)}
                                yearRange={`${addHours(new Date(), 3).getFullYear()}:${addYears(addHours(new Date(), 3), 3).getFullYear()}`}
                                showOnFocus={false}
                                pt={CalendarPague}
                                icon={<FaCalendarAlt className={
                                    classNames('px-4 rounded-r-lg',
                                        'w-[50px] h-full',
                                        'bg-sky-400 text-white hover:bg-sky-500',
                                        'dark:bg-sky-500 dark:text-black dark:hover:bg-sky-400'
                                    )}
                                />}
                                showIcon
                            />
                            <label htmlFor="recebivel_tipo_valor" className='mt-2 mb-2'>Valor total da cobrança</label>
                            <InputNumber
                                placeholder="ex: R$ 50"
                                prefix="R$ "
                                pt={InputNumberPague}
                                maxFractionDigits={2}
                                value={formState.recebivelTipoValor}
                                onValueChange={(e) => formState.setRecebivelTipoValor(e.value)}
                                required
                            />
                            <label htmlFor="recebivel_tipo_desconto_tipo" className='mt-2 mb-2'>Tipo de desconto</label>
                            <Dropdown
                                id='recebivel_tipo_desconto_tipo'
                                value={simulacaoState.recebivelTipoDescontoTipoSimula}
                                options={descontoOptions}
                                onChange={e => { actions.setRecebivelTipoDescontoTipoSimula(e.target.value); }}
                                pt={getRecebivelDropdownPt()}
                                required
                            />

                            <label htmlFor="recebivel_tipo_desconto" className='mt-2 mb-2'>Valor do Desconto</label>
                            <InputText
                                id="recebivel_tipo_desconto"
                                placeholder="ex: 10"
                                value={simulacaoState.recebivelTipoDescontoSimula}
                                onChange={(e) => actions.setRecebivelTipoDescontoSimula(e.target.value)}
                                type="number"
                                min={0}
                            />
                        </div>
                        <div className="flex flex-col w-full md:w-1/2 gap-2">
                            <label htmlFor="simula_parcela" className='mt-2 mb-2'>Numero de Parcelas</label>
                            <InputNumber
                                placeholder="ex: 12x"
                                inputId="simula_parcela"
                                pt={InputNumberPague}
                                min={1}
                                onValueChange={(e) => actions.setSimulaParcela(e.value)}
                                onInput={(e) => actions.handleSimulaParcelaInputChange?.(e.target.value)}
                                onKeyUp={(e) => actions.handleSimulaParcelaInputChange?.(e.target.value)}
                                value={simulacaoState.simulaParcela}
                                required
                            />

                            <SimulacaoResumoCard
                                recebivelTipoValor={formState.recebivelTipoValor}
                                descontoAtivado={simulacaoState.descontoAtivado}
                                parcelaSimulado={simulacaoState.parcelaSimulado}
                                simulaParcelaValida={simulacaoState.simulaParcelaValida}
                                simulaParcela={simulacaoState.simulaParcela}
                                valorParcela={simulacaoState.valorParcela}
                                mostraDateSimulado={simulacaoState.mostraDateSimulado}
                            />
                        </div>
                    </div>
                    <div className="w-full flex align-center justify-center">
                        <div className="w-full md:w-2/3 lg:w-1/3 mt-5">
                            <Button
                                severity='success'
                                label="Cadastrar Boleto Inteligente"
                                type="submit"
                                className="w-full"
                                onClick={onConfirmSimulacao}
                            />
                        </div>
                    </div>
                </div>
            </form>
        </Dialog>
    );
}
